import React from "react";
import Reveal from "@/components/Reveal";
import { useLocale } from "@/contexts/LocaleContext";
import { EditableT, EditableTLink, EditableImage } from "@/cms/EditableT";
import { useCmsEdit } from "@/cms/CmsEditContext";

const SOMMER_IMG =
  "https://api.builder.io/api/v1/image/assets/TEMP/ffe5553c21dcf1956a59c3c4298f391111f85d8d?width=674&format=webp&quality=75";
const WINTER_IMG =
  "https://api.builder.io/api/v1/image/assets/TEMP/5d82ed1478fa6c0eb3b63c5c3d15afd25248385e?width=676&format=webp&quality=75";
const TOP_EVENTS_IMG =
  "https://api.builder.io/api/v1/image/assets/TEMP/5272326e53bbeb4fa4823b3c6c8c559793896bb0?width=694&format=webp&quality=75";

type Tile = {
  id: string;
  href: string;
  src: string;
  labelKey: "home.erlebnisse.tile_sommer" | "home.erlebnisse.tile_top_events" | "home.erlebnisse.tile_winter";
  label: string;
  style: React.CSSProperties;
};

const ErlebnisseSection: React.FC = () => {
  const { t } = useLocale();
  const { isEditor, editMode } = useCmsEdit();
  const editing = isEditor && editMode;

  const tiles: Tile[] = [
    {
      id: "sommer",
      href: "/erlebnisse/sommer",
      src: SOMMER_IMG,
      labelKey: "home.erlebnisse.tile_sommer",
      label: t("home.erlebnisse.tile_sommer"),
      style: { left: "7.1719%", top: "0%", width: "45.6022%", height: "48.4615%" },
    },
    {
      id: "top-events",
      href: "/erlebnisse/top-events",
      src: TOP_EVENTS_IMG,
      labelKey: "home.erlebnisse.tile_top_events",
      label: t("home.erlebnisse.tile_top_events"),
      style: { left: "0%", top: "50%", width: "52.7741%", height: "50%" },
    },
    {
      id: "winter",
      href: "/erlebnisse/winter",
      src: WINTER_IMG,
      labelKey: "home.erlebnisse.tile_winter",
      label: t("home.erlebnisse.tile_winter"),
      style: { left: "54.2625%", top: "27.0513%", width: "45.7375%", height: "48.4615%" },
    },
  ];

  return (
    <section aria-labelledby="erlebnisse-heading" className="mt-[120px] md:mt-[180px] lg:mt-[247px]">
      <div className="mx-auto max-w-[1540px] px-6 md:px-10 lg:px-12 min-[1540px]:px-0">
        <div className="min-[1540px]:mr-auto min-[1540px]:max-w-[1437px] flex flex-col lg:flex-row lg:justify-between gap-12 lg:gap-8 xl:gap-12 items-start">
          {/* Text column – mobil zuerst */}
          <Reveal className="w-full lg:w-[42%] lg:max-w-[534px] order-1 lg:order-2 flex flex-col items-start lg:items-end gap-[40px] lg:gap-[55px] lg:pt-[40px] xl:pt-[98px]">
            <div className="flex flex-col items-start lg:items-end gap-[40px] lg:gap-[78px] w-full">
              <div className="flex flex-col items-start lg:items-end gap-[24px] lg:gap-[35px] w-full">
                <span className="block font-['Kumbh_Sans'] text-[#383637] text-[15px] md:text-[17px] lg:text-[19px] font-normal uppercase tracking-[0.25em] leading-[25px] text-left lg:text-right max-w-[400px]">
                  <EditableT k="home.erlebnisse.kicker" />
                </span>
                <h2
                  id="erlebnisse-heading"
                  className="font-['Kumbh_Sans'] text-[#383637] text-[28px] md:text-[36px] lg:text-[45px] font-extralight uppercase tracking-[0.05em] leading-[1.1] text-left lg:text-right max-w-[420px] [text-wrap:balance]"
                >
                  <EditableT k="home.erlebnisse.headline" multiline />
                </h2>
              </div>
              <p className="font-['Baskervville'] text-[#383637] text-[17px] leading-[28px] tracking-[0.02em] text-left lg:text-right">
                <EditableT k="home.erlebnisse.text" multiline />
              </p>
            </div>
            <EditableTLink
              k="home.erlebnisse.cta_label"
              href="/erlebnisse"
              className="inline-flex items-center justify-center h-[37px] px-[30px] rounded-full bg-[#6C184F] leading-none hover:bg-[#581340] transition-colors text-[#F2F2F2] font-['Kumbh_Sans'] text-[14px] font-medium uppercase tracking-[0.08em]"
            />
          </Reveal>

          {/* Image collage */}
          <Reveal delay={150} className="order-2 lg:order-1 relative w-full lg:flex-1 lg:max-w-[739px] aspect-[739/780] shrink-0">
            {tiles.map((tile) => (
              <div
                key={tile.id}
                style={tile.style}
                className="group absolute block overflow-hidden"
              >
                <EditableImage
                  id={`home.erlebnisse.tile_${tile.id}.image`}
                  src={tile.src}
                  alt={tile.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.08]"
                  wrapperClassName="absolute inset-0 block"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(56,54,55,0.00) 50%, rgba(56,54,55,0.55) 100%)",
                  }}
                />
                <span className="absolute inset-x-0 bottom-[22px] z-10 flex items-center justify-center gap-[8px] px-3 font-['Kumbh_Sans'] text-[11px] sm:text-[14px] font-medium uppercase tracking-[0.08em] text-[#F2F2F2] whitespace-nowrap">
                  <EditableTLink
                    k={tile.labelKey}
                    href={tile.href}
                    className="inline-flex items-center gap-[8px] text-[#F2F2F2]"
                  />
                  {!editing && (
                    <svg className="shrink-0" width="14" height="6" viewBox="0 0 14 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 3H14M11.4872 6L14 3L11.4872 0" stroke="#F2F2F2" />
                    </svg>
                  )}
                </span>
              </div>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ErlebnisseSection;
